import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { brokers } from "@/data/brokers";

const compareDropdownItems = [
  { label: "XM vs Tickmill", subtitle: "Bonus vs Pure ECN Cost", href: "/compare/xm-vs-tickmill" },
  { label: "XM vs FxPro", subtitle: "Traditional vs Modern", href: "/compare/xm-vs-fxpro" },
  { label: "XM vs IC Markets", subtitle: "For Manual vs EA Traders", href: "/compare/xm-vs-icmarkets" },
  { label: "XM vs Axi", subtitle: "Platform Performance Test", href: "/compare/xm-vs-axi" },
  { label: "XM vs FXTM", subtitle: "Loyalty & Bonus Comparison", href: "/compare/xm-vs-fxtm" },
];

export default function Navbar() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = () => {
    setMobileOpen(false);
    setCompareOpen(false);
  };

  const closeMobileMenu = () => {
    setMobileOpen(false);
    setCompareOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileOpen(!mobileOpen);
    if (mobileOpen) setCompareOpen(false);
  };

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-[#111827]/95 backdrop-blur-md shadow-lg shadow-black/20 border-b border-[#374151]/50"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0" onClick={handleNavClick}>
              <img src="/images/logo.png" alt="ForexChoiceLab" className="h-8 md:h-10 w-auto min-w-[180px] object-contain" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {/* Home Link */}
              <Link
                to="/"
                className={`px-4 py-2 text-sm font-medium transition-colors rounded-md ${location.pathname === "/" ? "text-[#8B5CF6] bg-[#1F2937]/70" : "text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1F2937]"}`}
                onClick={handleNavClick}
              >
                Home
              </Link>

              {/* Compare XM Mega Menu */}
              <div
                className="relative"
                onMouseEnter={() => setCompareOpen(true)}
                onMouseLeave={() => setCompareOpen(false)}
              >
                <Link
                  to="/compare"
                  className={`flex items-center gap-1 px-4 py-2 text-sm font-medium transition-colors rounded-md ${location.pathname.startsWith("/compare") ? "text-[#8B5CF6] bg-[#1F2937]/70" : "text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1F2937]"}`}
                >
                  Compare XM
                  <i className={`ri-arrow-down-s-line transition-transform ${compareOpen ? "rotate-180" : ""}`}></i>
                </Link>
                {compareOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-[420px] bg-[#1F2937] border border-[#374151] rounded-lg shadow-xl shadow-black/40 overflow-hidden">
                    <div className="p-3">
                      <div className="text-xs font-semibold text-[#64748B] uppercase tracking-wider px-3 py-2">
                        Broker Comparisons
                      </div>
                      <div className="grid gap-1">
                        {compareDropdownItems.map((item) => (
                          <Link
                            key={item.label}
                            to={item.href}
                            className={`flex items-center justify-between px-3 py-3 rounded-md transition-colors group ${location.pathname === item.href ? "bg-[#1F2937]" : "hover:bg-[#374151]"}`}
                            onClick={handleNavClick}
                          >
                            <div>
                              <div className={`text-sm font-medium transition-colors ${location.pathname === item.href ? "text-[#8B5CF6]" : "text-[#F8FAFC] group-hover:text-[#7C3AED]"}`}>
                                {item.label}
                              </div>
                              <div className="text-xs text-[#64748B] mt-0.5">{item.subtitle}</div>
                            </div>
                            <i className={`ri-arrow-right-line transition-colors ${location.pathname === item.href ? "text-[#8B5CF6]" : "text-[#64748B] group-hover:text-[#7C3AED]"}`}></i>
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Platform Rankings */}
              <Link
                to="/rankings"
                className={`px-4 py-2 text-sm font-medium transition-colors rounded-md ${location.pathname === "/rankings" ? "text-[#8B5CF6] bg-[#1F2937]/70" : "text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1F2937]"}`}
                onClick={handleNavClick}
              >
                Platform Rankings
              </Link>

              {/* Forex Academy */}
              <Link
                to="/academy"
                className={`px-4 py-2 text-sm font-medium transition-colors rounded-md ${location.pathname === "/academy" ? "text-[#8B5CF6] bg-[#1F2937]/70" : "text-[#94A3B8] hover:text-[#F8FAFC] hover:bg-[#1F2937]"}`}
                onClick={handleNavClick}
              >
                Forex Academy
              </Link>
            </div>

            {/* CTA Button */}
            <div className="hidden md:block">
              <a
                href={brokers.xm.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#7C3AED] hover:bg-[#8B5CF6] text-white text-sm font-semibold rounded-md transition-all hover:shadow-lg hover:shadow-[#7C3AED]/20 whitespace-nowrap"
              >
                Open XM Account
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>

            {/* Mobile Hamburger */}
            <button
              className="md:hidden w-10 h-10 flex items-center justify-center text-[#F8FAFC]"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-xl`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Panel */}
      <div
        className={`fixed inset-0 z-40 md:hidden transition-all duration-300 ${
          mobileOpen ? "visible" : "invisible"
        }`}
      >
        <div
          className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity ${
            mobileOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={closeMobileMenu}
        />
        <div
          className={`absolute right-0 top-0 h-full w-[280px] bg-[#1F2937] border-l border-[#374151] shadow-2xl transition-transform duration-300 ${
            mobileOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="pt-20 px-5 pb-8 h-full overflow-y-auto">
            {/* Mobile Regular Links */}
            <Link
              to="/"
              className={`block py-3 font-medium ${location.pathname === "/" ? "text-[#8B5CF6]" : "text-[#F8FAFC]"}`}
              onClick={handleNavClick}
            >
              Home
            </Link>

            {/* Mobile Compare XM */}
            <div className="mb-2">
              <Link
                to="/compare"
                className={`block py-3 font-medium ${location.pathname.startsWith("/compare") ? "text-[#8B5CF6]" : "text-[#F8FAFC]"}`}
                onClick={handleNavClick}
              >
                Compare XM Hub
              </Link>
              <button
                onClick={() => setCompareOpen(!compareOpen)}
                className="w-full flex items-center justify-between py-3 text-[#F8FAFC] font-medium"
              >
                Individual Comparisons
                <i className={`ri-arrow-down-s-line transition-transform ${compareOpen ? "rotate-180" : ""}`}></i>
              </button>
              {compareOpen && (
                <div className="pl-4 border-l-2 border-[#7C3AED]/30 space-y-1">
                  {compareDropdownItems.map((item) => (
                    <Link
                      key={item.label}
                      to={item.href}
                      className={`block py-2.5 text-sm ${location.pathname === item.href ? "text-[#F8FAFC]" : "text-[#94A3B8] hover:text-[#F8FAFC]"}`}
                      onClick={handleNavClick}
                    >
                      <div className="font-medium">{item.label}</div>
                      <div className="text-xs text-[#64748B]">{item.subtitle}</div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/rankings"
              className={`block py-3 font-medium ${location.pathname === "/rankings" ? "text-[#8B5CF6]" : "text-[#F8FAFC]"}`}
              onClick={handleNavClick}
            >
              Platform Rankings
            </Link>

            <Link
              to="/academy"
              className={`block py-3 font-medium ${location.pathname === "/academy" ? "text-[#8B5CF6]" : "text-[#F8FAFC]"}`}
              onClick={handleNavClick}
            >
              Forex Academy
            </Link>

            <div className="mt-6 pt-6 border-t border-[#374151]">
              <a
                href={brokers.xm.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-[#7C3AED] hover:bg-[#8B5CF6] text-white text-sm font-semibold rounded-md transition-colors"
              >
                Open XM Account
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}